import { useId, useState, useMemo } from 'react'
import { Label } from '@/components/ui/label'
import { CountryCombobox } from '@/components/ui/country-combobox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'

type Country = { id: string; code: string; name: string }
type State = { id: string; code: string; name: string; country_id: string }
type LocalArea = { id: string; code: string; name: string; state_id: string }

type Props = {
  countries: Country[]
  states: State[]
  localAreas: LocalArea[]
  countryCode: string
  stateProvince: string
  city: string
  onChange: (fields: { country_code: string; state_province: string; city: string }) => void
}

export function LocationSelector({ countries, states, localAreas, countryCode, stateProvince, city, onChange }: Props) {
  const countryId = useId()
  const [stateSearch, setStateSearch] = useState('')
  const [citySearch, setCitySearch] = useState('')

  const selectedCountry = countries.find(c => c.code === countryCode)
  const filteredStates = useMemo(() => {
    if (!selectedCountry) return []
    return states
      .filter(s => s.country_id === selectedCountry.id)
      .filter(s => s.name.toLowerCase().includes(stateSearch.toLowerCase()))
  }, [states, selectedCountry, stateSearch])

  const selectedState = states.find(s => s.code === stateProvince && s.country_id === selectedCountry?.id)
  const filteredCities = useMemo(() => {
    if (!selectedState) return []
    return localAreas
      .filter(la => la.state_id === selectedState.id)
      .filter(la => la.name.toLowerCase().includes(citySearch.toLowerCase()))
  }, [localAreas, selectedState, citySearch])

  const handleCountryChange = (code: string) => {
    onChange({ country_code: code, state_province: '', city: '' })
    setStateSearch('')
    setCitySearch('')
  }

  const handleStateChange = (code: string) => {
    onChange({ country_code: countryCode, state_province: code, city: '' })
    setCitySearch('')
  }

  const handleCityChange = (name: string) => {
    onChange({ country_code: countryCode, state_province: stateProvince, city: name })
  }

  return (
    <div className="space-y-3">
      {/* Country */}
      <div className="space-y-1.5">
        <Label htmlFor={countryId} className="text-xs text-muted-foreground">Country</Label>
        <CountryCombobox
          id={countryId}
          countries={countries}
          value={countryCode}
          onValueChange={handleCountryChange}
          className="h-8 text-sm"
          placeholder="Select country"
        />
      </div>

      {/* State / Province */}
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">State / Province</Label>
        {filteredStates.length > 0 ? (
          <Select value={stateProvince} onValueChange={handleStateChange} disabled={!countryCode}>
            <SelectTrigger className="h-8 text-sm">
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent>
              <div className="p-1">
                <Input
                  placeholder="Search states..."
                  value={stateSearch}
                  onChange={e => setStateSearch(e.target.value)}
                  className="h-7 text-xs mb-1"
                  onClick={e => e.stopPropagation()}
                />
              </div>
              {filteredStates.map(s => (
                <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            value={stateProvince}
            onChange={e => onChange({ country_code: countryCode, state_province: e.target.value, city })}
            placeholder="Enter state or province"
            className="h-8 text-sm"
            disabled={!countryCode}
          />
        )}
      </div>

      {/* City */}
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">City</Label>
        {filteredCities.length > 0 ? (
          <Select value={city} onValueChange={handleCityChange} disabled={!stateProvince}>
            <SelectTrigger className="h-8 text-sm">
              <SelectValue placeholder="Select city" />
            </SelectTrigger>
            <SelectContent>
              <div className="p-1">
                <Input
                  placeholder="Search cities..."
                  value={citySearch}
                  onChange={e => setCitySearch(e.target.value)}
                  className="h-7 text-xs mb-1"
                  onClick={e => e.stopPropagation()}
                />
              </div>
              {filteredCities.map(la => (
                <SelectItem key={la.code} value={la.name}>{la.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Input
            value={city}
            onChange={e => onChange({ country_code: countryCode, state_province: stateProvince, city: e.target.value })}
            placeholder="Enter city"
            className="h-8 text-sm"
            disabled={!stateProvince}
          />
        )}
      </div>
    </div>
  )
}
